import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { loginUser, loginUserSSO, sendResetPasswordEmail, wakeUpServer } from '../actions/auth';
import { TextField, Button, Typography, Box, Divider, Dialog, DialogActions, DialogContent, DialogTitle, CircularProgress, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useAlert } from '../contexts/AlertContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFirstRequest, setIsFirstRequest] = useState(true);
  const [serverWokenUp, setServerWokenUp] = useState(false);
  const navigate = useNavigate();
  const { loginWithRedirect, isAuthenticated, getIdTokenClaims } = useAuth0();
  const { showAlert } = useAlert();
  const theme = useTheme();

  useEffect(() => {
    const wakeServer = async () => {
      const success = await wakeUpServer();
      setServerWokenUp(success);
    };
    wakeServer();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    if (isFirstRequest && !serverWokenUp) {
      showAlert('Initial request may take a few seconds to load. Please be patient.', 'info');
      setIsFirstRequest(false);
    }
    try {
      const { token, user } = await loginUser(email, password);
      Cookies.set('token', token, { secure: true, sameSite: 'Strict' });
      Cookies.set('user', user, { secure: true, sameSite: 'Strict' });
      showAlert('Login successful!', 'success');
      navigate('/home');
    } catch (err) {
      // Show server-specific error message
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Login failed. Please check your credentials.';
      showAlert(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendResetEmail = async () => {
    if (!resetEmail.trim()) {
      showAlert('Email is required', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const response = await sendResetPasswordEmail(resetEmail.trim());
      showAlert(
        response?.message || 'If you have registered with us, you will receive an email with password reset instructions. Please check your inbox and spam/junk folder.',
        'success'
      );
      setResetDialogOpen(false);
      setResetEmail('');
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to send reset email.';
      showAlert(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const saveUser = async () => {
      setIsLoading(true);
      try {
        if (isAuthenticated) {
          const idToken = await getIdTokenClaims();
          const { token, username } = await loginUserSSO(idToken.__raw);
          Cookies.set('token', token, { secure: true, sameSite: 'Strict' });
          Cookies.set('user', username, { secure: true, sameSite: 'Strict' });
          showAlert('SSO login successful!', 'success');
          navigate('/home');
        }
      } catch (err) {
        // Show server-specific error message
        const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to save SSO user.';
        showAlert(errorMessage, 'error');
      } finally {
        setIsLoading(false);
      }
    };

    if (isAuthenticated) {
      saveUser();
    }
  }, [isAuthenticated, navigate, getIdTokenClaims, showAlert]);

  const handleStoreDownload = (storeUrl) => {
    window.open(storeUrl, '_blank');
  };

  return (
    <Box sx={{ 
      minHeight: '85vh',
      display: 'flex',
      flexDirection: 'column',
      background: theme.palette.mode === 'dark' ? '#121212' : '#ffffff',
      position: 'relative'
    }}>
      {/* Main Content */}
      <Box sx={{ 
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3
      }}>
        <Box sx={{ 
          maxWidth: 450,
          width: '100%',
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          borderRadius: 4,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
            : '0 8px 32px rgba(0, 0, 0, 0.1)',
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(255, 255, 255, 0.2)',
          p: 4
        }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography 
              variant="h3" 
              gutterBottom 
              sx={{ 
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 1
              }}
            >
              Welcome Back
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ fontSize: '1.1rem' }}
            >
              Sign in to your account to continue
            </Typography>
          </Box>

          {/* Login Form */}
          <form onSubmit={handleLogin}>
            <TextField
              label="Email"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              tabIndex={1}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)' 
                    : 'rgba(0, 0, 0, 0.02)',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.08)' 
                      : 'rgba(0, 0, 0, 0.04)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'rgba(0, 0, 0, 0.06)',
                  }
                }
              }}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              tabIndex={2}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)' 
                    : 'rgba(0, 0, 0, 0.02)',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.08)' 
                      : 'rgba(0, 0, 0, 0.04)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'rgba(0, 0, 0, 0.06)',
                  }
                }
              }}
            />
            <Box sx={{ textAlign: 'right', mt: 1, mb: 2 }}>
              <Button
                variant="text"
                color="primary"
                sx={{ textTransform: 'none', fontWeight: 500, fontSize: '0.95rem', p: 0, minWidth: 0 }}
                onClick={() => setResetDialogOpen(true)}
                disabled={isLoading}
                tabIndex={3}
              >
                Forgot password?
              </Button>
            </Box>
            
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress size={60} />
              </Box>
            ) : (
              <>
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary" 
                  sx={{ 
                    mt: 3, 
                    mb: 2,
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease'
                  }} 
                  fullWidth
                  tabIndex={4}
                >
                  Sign In
                </Button>
                
                <Divider sx={{ my: 3, position: 'relative' }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      px: 2,
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(255, 255, 255, 0.9)',
                      borderRadius: 1
                    }}
                  >
                    OR
                  </Typography>
                </Divider>
                
                <Button 
                  onClick={() => loginWithRedirect()} 
                  variant="outlined" 
                  color="primary" 
                  fullWidth
                  tabIndex={5}
                  sx={{ 
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      transform: 'translateY(-1px)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Continue with SSO
                </Button>
                
                <Typography sx={{ mt: 3, textAlign: 'center' }}>
                  Don't have an account?{' '}
                  <Link 
                    to="/register" 
                    style={{ 
                      color: theme.palette.primary.main,
                      textDecoration: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    Create one here
                  </Link>
                </Typography>
              </>
            )}
          </form>

          {/* Reset Password Dialog */}
          <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogContent>
              <Typography>
                Enter your email address below. If you have registered with us, you will receive password reset instructions. Check your inbox and spam folder.
              </Typography>
              <TextField
                label="Email"
                fullWidth
                margin="normal"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
                disabled={isLoading}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setResetDialogOpen(false)} disabled={isLoading}>Cancel</Button>
              <Button 
                onClick={handleSendResetEmail} 
                color="primary"
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>

      {/* Footer with Store Badges and Links */}
      <Box sx={{ 
        p: 3,
        textAlign: 'center'
      }}>
        {/* Store Download Badges */}
        <Box sx={{ 
          display: 'flex', 
          gap: 3, 
          justifyContent: 'center', 
          flexWrap: 'wrap', 
          alignItems: 'center', 
          mb: 3 
        }}>
          {/* Apple App Store Badge */}
          <Box 
            component="img"
            src={theme.palette.mode === 'dark' 
              ? "/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg"
              : "/Download_on_the_App_Store_Badge_US-UK_RGB_wht_092917.svg"
            }
            alt="Download on the App Store"
            onClick={() => handleStoreDownload('https://apps.apple.com/us/app/event-plannnig-app/id6747792111')}
            sx={{ 
              height: 50, 
              cursor: 'pointer',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': {
                transform: 'scale(1.05)'
              }
            }}
          />
          
          {/* Microsoft Store Badge */}
          <Box
            component="a"
            href="https://apps.microsoft.com/detail/9NP2G8Q29V1K?referrer=appbadge&mode=direct"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none'
            }}
          >
            <Box
              component="img"
              src={theme.palette.mode === 'dark' 
                ? "https://get.microsoft.com/images/en-us%20dark.svg"
                : "https://get.microsoft.com/images/en-us%20light.svg"
              }
              alt="Get it from Microsoft"
              sx={{ 
                height: 50,
                cursor: 'pointer',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.05)'
                },
                border: '1.5px solid ' + theme.palette.text.secondary,
                borderRadius: '10px'
              }}
            />
          </Box>
        </Box>
        
        {/* Footer Links */}
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'center',
          gap: 3
        }}>
          <Link 
            to="/support" 
            style={{ 
              color: theme.palette.text.secondary, 
              textDecoration: 'none',
              fontSize: '0.9rem',
              fontWeight: 500,
              transition: 'color 0.2s ease-in-out',
            }}
            onMouseEnter={(e) => e.target.style.color = theme.palette.primary.main}
            onMouseLeave={(e) => e.target.style.color = theme.palette.text.secondary}
          >
            Support
          </Link>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: '0.9rem' }}
          >
            |
          </Typography>
          <Link 
            to="/privacy" 
            style={{ 
              color: theme.palette.text.secondary, 
              textDecoration: 'none',
              fontSize: '0.9rem',
              fontWeight: 500,
              transition: 'color 0.2s ease-in-out',
            }}
            onMouseEnter={(e) => e.target.style.color = theme.palette.primary.main}
            onMouseLeave={(e) => e.target.style.color = theme.palette.text.secondary}
          >
            Privacy Policy
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
